import { useRef, useState } from 'react';

import ReactDOM from 'react-dom';

import { useAppDispatch } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { ReactComponent as QuestionMark } from 'assets/images/active-question.svg';

import useCoordinates from 'utils/useCoordinates';

import { trackHeapEvent } from 'dux/tracking/actions';

// Similar to existing ModalPortal but more encapsulated (we do not export the Portal itself)
const TooltipPortal = ({
  children,
  isOpen = false,
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) => isOpen && document && ReactDOM.createPortal(children, document.getElementById('tooltip')!);

// Inspired by Components/Tooltip but configurable
const TooltipBox = styled.div<{
  topPlus: number;
  arrowsize: number;
  top: number;
  left: number;
  arrowshift: number;
  maxwidth: number;
  shadow: keyof typeof legacyTheme.shadows;
  hasBeforePointer?: boolean;
}>`
  position: absolute;
  top: ${(props) => props.topPlus + props.top + props.arrowsize * 2}px;
  left: ${(props) => props.left - props.arrowshift}px;
  transform: translate3d(-50%, 0, 0);

  width: 100%;
  max-width: ${(props) => props?.maxwidth}px;
  padding: 32px;

  background-color: ${theme.colors.neutral[100]};
  box-shadow: ${(props) => legacyTheme.shadows?.[props?.shadow]};

  &::before {
    content: '';

    position: absolute;
    top: ${(props) => `calc(-${props?.arrowsize}px / 2)`};
    left: ${(props) => `calc(50% - ${props?.arrowsize}px / 2 + ${props?.arrowshift}px)`};
    transform: rotate(135deg);

    display: ${(props) => (props?.hasBeforePointer ? 'block' : 'none')};

    width: ${(props) => props?.arrowsize}px;
    height: ${(props) => props?.arrowsize}px;

    background-color: ${theme.colors.neutral[100]};
    box-shadow: -2px 2px 5px -2px rgb(150 150 150 / 20%);
  }
`;

const TooltipOpener = styled.span<{ width: number; height: number; variant?: string }>`
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  padding: 0;

  border: none;
  background-color: transparent;

  font: inherit;
  color: inherit;
  cursor: pointer;

  > svg {
    width: ${(props) => props.width}px;
    height: ${(props) => props.height}px;
  }

  ${({ variant }) =>
    variant === 'noir'
      ? `
    & svg g {
      fill: ${theme.colors.primary[400]};
      stroke-width: 0;
    }

    & svg g path {
      fill: ${theme.colors.neutral[100]};
    }`
      : ''}
`;

const TooltipBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;

  width: 100%;
  height: 100%;
`;

type TooltipProps = {
  arrowShift?: number;
  arrowSize?: number;
  content: React.ReactNode;
  dataClick?: string;
  dataFrom?: string;
  dataTestid?: string;
  hasBeforePointer?: boolean;
  maxWidth: number;
  openerComponent?: React.ReactNode;
  openerHeight?: number;
  openerVariant?: 'noir';
  openerWidth?: number;
  shadowVariant?: keyof typeof legacyTheme.shadows;
  style?: React.CSSProperties;
  topPlus?: number;
  trackingText?: string;
};

export const Tooltip = ({
  arrowShift = 0,
  arrowSize = 20,
  content,
  dataClick,
  dataFrom,
  dataTestid,
  hasBeforePointer = true,
  maxWidth = 360,
  openerComponent = <QuestionMark />,
  openerHeight = 22,
  openerVariant,
  openerWidth = 22,
  shadowVariant = 'socialImpact',
  style = {},
  topPlus = 0,
  trackingText = '',
}: TooltipProps) => {
  const dispatch = useAppDispatch();
  const [isOpen, setOpen] = useState(false);

  // Initialize computed values
  let calculatedMaxWidth = maxWidth;
  let leftShift = 0;
  let rightShift = 0;

  const tooltipOpenerRef = useRef<HTMLElement>(null);
  const tooltipOpenerCoordinates = useCoordinates(isOpen ? tooltipOpenerRef : null);
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  // Calculate computed values depending on window
  if (maxWidth > windowWidth) {
    // Maxium tooltip box is window width
    calculatedMaxWidth = windowWidth;
  }

  // This can only be calculated after ref and bounding box of opener exists
  if (tooltipOpenerCoordinates) {
    const centerOfToolTipOpener = tooltipOpenerCoordinates.x + tooltipOpenerCoordinates.width / 2;
    // Calculate left tooltip box boundary
    const leftBound = centerOfToolTipOpener - calculatedMaxWidth / 2 - arrowShift;

    if (leftBound < 0) {
      // If the left bound is outside of the screen we shift it
      leftShift = leftBound;
    }

    // Calculate right tooltip box boundary
    const rightBound =
      centerOfToolTipOpener -
      (arrowShift + leftShift) + // Important to take leftShift into account as well here
      calculatedMaxWidth / 2;

    if (rightBound > windowWidth) {
      /**
       * If the right bound is outside of the screen we shift it
       * 24 is an offset to the window edge
       */
      rightShift = rightBound - windowWidth + 24;
    }
  }

  const onActivate = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    if (trackingText) {
      dispatch(trackHeapEvent(`${trackingText} clicked`));
    }
    e.stopPropagation();
    e.preventDefault();
    setOpen(!isOpen);
  };

  return (
    <>
      <TooltipOpener
        ref={tooltipOpenerRef}
        data-click={dataClick}
        data-from={dataFrom}
        data-testid={`tooltip-opener${dataTestid ? `-${dataTestid}` : ''}`}
        height={openerHeight}
        id="tooltip-opener"
        onClick={onActivate}
        onKeyDown={onActivate}
        role="button"
        tabIndex={0}
        variant={openerVariant}
        width={openerWidth}
      >
        {openerComponent}
      </TooltipOpener>

      <TooltipPortal isOpen={isOpen}>
        <TooltipBackdrop
          data-testid={`tooltip-backdrop-${dataTestid}`}
          onClick={(e) => {
            e.stopPropagation(); // Too buggy without this
            setOpen(false);
          }}
          role="presentation"
        >
          {tooltipOpenerCoordinates && (
            <TooltipBox
              arrowshift={arrowShift + leftShift + rightShift}
              arrowsize={arrowSize}
              color="grey"
              data-testid={`tooltip-content${dataTestid ? `-${dataTestid}` : ''}`}
              hasBeforePointer={hasBeforePointer}
              left={tooltipOpenerCoordinates.x + tooltipOpenerCoordinates.width / 2}
              maxwidth={calculatedMaxWidth}
              onClick={(e) => {
                e.stopPropagation(); // To avoid closing the tooltip
              }}
              shadow={shadowVariant}
              style={style}
              top={tooltipOpenerCoordinates.y}
              topPlus={topPlus}
            >
              {content}
            </TooltipBox>
          )}
        </TooltipBackdrop>
      </TooltipPortal>
    </>
  );
};
